import parseHTML from '../utils/_parseHtml';
import debounce from '../utils/_debounce';
import wishlist from '../utils/_wishlist';

const DEBOUNCE_USER_INPUT_DURATION = 300;
const MIN_CARACTERS_BEFORE_SEARCH = 3;

class oSearch extends HTMLDivElement {
    constructor() {
        super();
        this.form = this.querySelector('form');
        this.input = this.querySelector('input');
        this.inputResetBtn = this.querySelector('.reset-button');
        this.suggestionUrl = this.dataset.suggestionUrl;
        this.searchSuggestionContainer = this.querySelector('.suggestions-wrapper');
        this.mostSearchedContainer = this.querySelector('.most-searched-wrapper');
        this.noResultContainer = this.querySelector('.no-result-wrapper');
        this.sidebar = this.closest('[is="o-sidebar-ajax"]');
        this.loader = this.querySelector('.a-loader-black');
        this.sidebarGenderFilters = this.querySelectorAll('.gender-filter');
        this.fullPageLinkContainer = this.sidebar.querySelector('section.footer');
        this.fullPageLinkBtn = this.fullPageLinkContainer.querySelector('a');
        this.noSearchCarousel = this.querySelector('#no-search-sidebar-cross-sell');

        // bind this
        this.onInputChange = this.onInputChange.bind(this);
        this.debouncedInputChange = this.debouncedInputChange.bind(this);
        this.onGenderFiltersChange = this.onGenderFiltersChange.bind(this);
        this.onResetBtn = this.onResetBtn.bind(this);
        this.removeActiveGenderRefinements = this.removeActiveGenderRefinements.bind(this);
    }

    connectedCallback() {
        this.input.addEventListener('input', debounce(this.debouncedInputChange, DEBOUNCE_USER_INPUT_DURATION));
        this.input.addEventListener('input', this.onInputChange);

        for (let sidebarGenderFilter of this.sidebarGenderFilters) {
            sidebarGenderFilter.addEventListener('click', this.onGenderFiltersChange);
        }

        this.inputResetBtn.addEventListener('click', this.onResetBtn);
        this.input.focus();
    }

    onGenderFiltersChange(e) {
        for (let sidebarGenderFilter of this.sidebarGenderFilters) {
            if (e.currentTarget !== sidebarGenderFilter) sidebarGenderFilter.classList.remove('active');
        }
        e.currentTarget.classList.toggle('active');

        this.input.dispatchEvent(new Event('input'));
    }

    removeActiveGenderRefinements() {
        var activeGenderFilter = this.querySelector('.gender-filter.active');

        if (activeGenderFilter) activeGenderFilter.classList.remove('active');
    }

    /*
        Searching is managed on debouncedInputChange below
        This only clear the search results or tell the user to wait during loading…
    */
    onInputChange() {
        if (this.input.value.length >= MIN_CARACTERS_BEFORE_SEARCH) {
            if (this.noSearchCarousel) {
                this.noSearchCarousel.setAttribute('hidden', true);
            }
            // abort ongoing fetch
            if (this.controller) {
                this.controller.abort();
            }
            this.loader.removeAttribute('hidden');
            this.searchSuggestionContainer.setAttribute('hidden', true);
            this.noResultContainer.setAttribute('hidden', true);
            if (this.mostSearchedContainer) this.mostSearchedContainer.setAttribute('hidden', true);
        } else {
            if (this.noSearchCarousel) {
                this.noSearchCarousel.removeAttribute('hidden');
            }
            if (this.mostSearchedContainer) this.mostSearchedContainer.removeAttribute('hidden');
            this.searchSuggestionContainer.setAttribute('hidden', true);
            this.searchSuggestionContainer.innerHTML = '';
            this.noResultContainer.setAttribute('hidden', true);
            this.loader.setAttribute('hidden', true);
        }
        // deal with cancel button
        this.toggleCancelButton(this.input.value.length);
        this.fullPageLinkContainer.setAttribute('hidden', true);
    }

    toggleCancelButton(show) {
        if (show) {
            this.inputResetBtn.removeAttribute('hidden');
            this.input.style.paddingInlineEnd = (this.inputResetBtn.offsetWidth + 20) + 'px';
        } else {
            this.inputResetBtn.setAttribute('hidden', true);
            this.input.style.paddingInlineEnd = '1.25rem';
        }
    }

    /*
        This happens ${DEBOUNCE_USER_INPUT_DURATION} ms after debounced user input (also after onInputChange() above)
    */
    debouncedInputChange() {
        if (this.input.value.length >= MIN_CARACTERS_BEFORE_SEARCH) {
            var searchSuggestionUrl = new URL(this.suggestionUrl);

            searchSuggestionUrl.searchParams.append('q', this.input.value);
            searchSuggestionUrl.searchParams.append('search-sidebar', true);

            // controller to abort the fetch request see https://davidwalsh.name/cancel-fetch
            this.controller = new AbortController(); // eslint-disable-line no-undef
            const { signal } = this.controller;

            // Fetch Search Suggestions in parallel
            fetch(searchSuggestionUrl.toString(), {
                method: 'GET',
                signal
            }).then((response) => {
                return response.text();
            }).then((responseHtml) => {
                var htmlElement = parseHTML(responseHtml);
                var responsePagination = htmlElement.querySelector('.js-pagination');

                this.searchSuggestionContainer.innerHTML = responseHtml;
                // Depending on result/no-result, add specific class and display gender refinements filters or not
                if (responsePagination) {
                    var fullPageUrl = responsePagination.dataset.currentUrl;
                    var btnWording = responsePagination.dataset.seeXProducts;
                    this.fullPageLinkBtn.innerText = btnWording;
                    this.fullPageLinkBtn.href = fullPageUrl;
                    this.fullPageLinkContainer.removeAttribute('hidden');
                    this.searchSuggestionContainer.removeAttribute('hidden');
                    this.noResultContainer.setAttribute('hidden', true);
                } else {
                    this.fullPageLinkContainer.setAttribute('hidden', true);
                    this.noResultContainer.removeAttribute('hidden');
                }

                wishlist.activateWishlistHeartButtons(); // Activate wishlist button for new loaded products
                this.loader.setAttribute('hidden', true);

                if (this.mostSearchedContainer) this.mostSearchedContainer.setAttribute('hidden', true);
            }).catch(() => {}); // do nothing (this just prevents console error on aborted fetch)
        }
    }

    onResetBtn() {
        // button type="reset" will clear the search input natively
        this.removeActiveGenderRefinements();
        this.fullPageLinkContainer.setAttribute('hidden', true);
        this.noResultContainer.setAttribute('hidden', true);
        if (this.mostSearchedContainer) this.mostSearchedContainer.removeAttribute('hidden');
        this.loader.setAttribute('hidden', true);
        this.searchSuggestionContainer.innerHTML = '';
        if (this.controller) {
            this.controller.abort();
        }
        this.toggleCancelButton(false);
        this.input.focus();
    }
}

customElements.define('o-search', oSearch, {
    extends: 'div'
});
